import styled from "@emotion/styled";
import { Typography } from "../Typography";
import { ImpactTypeIcon } from "../ImpactTypeIcon";
import {
  ProjectType,
  getProjectMapUrl,
  Wordpress,
  ImpactType,
  getCountryRegionFromLocation,
} from "@ecologi/shared";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import ProjectIcon from "@ecologi/assets/icons/book.svg";
import Sapling from "@ecologi/assets/icons/sapling.svg";
import Cloud from "@ecologi/assets/icons/carbon-removal-cloud.svg";
import SingleWildflower from "@ecologi/assets/icons/single-wildflower.svg";
import CarbonIcon from "@ecologi/assets/icons/carbon-avoidance-logo.svg";
import { DarkLinkText } from "../HowCalculatedModal/HowCalculatedModal";

// Type with title string, location string, project string and quantity number
export type ImpactCardProps = {
  title: string;
  location: Wordpress.ProjectLocation;
  slug: string;
  projectType: ProjectType;
  quantity: string;
  impactType: ImpactType;
};

const TitleText = styled(Typography)`
  text-decoration: none;
  max-width: 272px;
`;

const ImpactIcon = styled(ImpactTypeIcon)`
  margin-right: 16px;
  margin-top: 4px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  grid-area: title;
  margin-bottom: 28px;
`;

const Card = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.mistLight};
  border-radius: 24px;
  padding: 26px 28px;
  display: grid;
  grid-template-columns: 70% auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    "title quantityLabel"
    "links quantity";
  @media ${({ theme }) => theme.bp.tabletDown} {
    grid-template-columns: auto;
    grid-template-rows: auto auto;
    grid-template-areas:
      "title title"
      "links links"
      "quantityLabel quantity";
  }
  min-height: 168px;
`;

const StyledSapling = styled(Sapling)`
  height: 32px;
  width: 32px;
`;

const StyledProjectIcon = styled(ProjectIcon)`
  margin: auto;
`;

const StyledLocationIcon = styled(LocationIcon)`
  margin: auto;
`;

const GridContainer = styled.div`
  grid-area: links;
  display: grid;
  grid-template-columns: 25px 1fr;
  align-items: flex-start;
  gap: 6px 12px;
  align-self: end;
`;

const QuantityContainer = styled.div`
  grid-area: quantity;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  justify-self: end;
  align-self: end;
  @media ${({ theme }) => theme.bp.tabletDown} {
    margin-top: 32px;
  }
`;

const QuantityLabel = styled(Typography)`
  font-weight: 800 !important;
`;

const QuantityDescription = styled(Typography)`
  justify-self: end;
  line-height: 29px !important;
  white-space: nowrap;
  @media ${({ theme }) => theme.bp.tabletDown} {
    align-self: end;
    justify-self: start;
  }
`;

const COLabel = (
  <>
    CO<sub>2</sub>e
  </>
);

const impactQuantityLabels: Record<ImpactType, React.ReactNode> = {
  [ImpactType.TREES]: "Number of trees funded",
  [ImpactType.CARBON_OFFSETS]: <>Tonnes {COLabel} avoided</>,
  [ImpactType.CARBON_REMOVAL]: <>Tonnes {COLabel} removed</>,
  [ImpactType.HABITAT_RESTORATION]: <>m² of habitat restored</>,
};

const impactQuantityIcon: Record<ImpactType, React.ReactNode> = {
  [ImpactType.TREES]: <StyledSapling />,
  [ImpactType.CARBON_OFFSETS]: <CarbonIcon />,
  [ImpactType.CARBON_REMOVAL]: <Cloud />,
  [ImpactType.HABITAT_RESTORATION]: <SingleWildflower />,
};

export const ImpactCard = ({
  title,
  location,
  slug,
  quantity,
  projectType,
  impactType,
}: ImpactCardProps) => {
  const projectMapUrl = getProjectMapUrl(location);
  const { country } = getCountryRegionFromLocation(location.name);

  return (
    <Card data-testid="impact-card">
      <TitleContainer>
        <ImpactIcon projectType={projectType} />
        <TitleText use="standaloneLink">{title}</TitleText>
      </TitleContainer>

      <QuantityDescription use="body3">
        {impactQuantityLabels[impactType]}
      </QuantityDescription>

      <GridContainer>
        {location.name && (
          <>
            <StyledLocationIcon />
            {projectMapUrl ? (
              <DarkLinkText
                as={"a"}
                use="inlineLink"
                target={"_blank"}
                href={projectMapUrl}
              >
                {country}
              </DarkLinkText>
            ) : (
              <Typography use="body1">{country}</Typography>
            )}
          </>
        )}
        <StyledProjectIcon />
        <DarkLinkText
          as={"a"}
          target={"_blank"}
          use="inlineLink"
          href={`/projects/${slug}`}
        >
          Read more about this project
        </DarkLinkText>
      </GridContainer>

      <QuantityContainer>
        <QuantityLabel use="body3" data-testid="project-quantity-label">
          {Number(quantity).toLocaleString(undefined, {
            maximumFractionDigits: 4,
          })}
        </QuantityLabel>
        {impactQuantityIcon[impactType]}
      </QuantityContainer>
    </Card>
  );
};
